import { c as _c } from "react/compiler-runtime";
// Server-rendered industry page sections.
//
// Splitting note: previously this file was the only home for every industry
// section AND was marked `'use client'` because two of those sections
// (TabbedSection, GalleryCarousel) needed React state. That forced the
// entire industry page to hydrate on the client even though the bulk of the
// content (hero copy, intro, benefits, decoder, resources, final-quote copy)
// is purely static and SSR-friendly.
//
// Interactive sections now live in IndustryInteractiveSections.tsx (the only
// `'use client'` file). They are re-exported below so existing PageContent
// imports keep working unchanged.

import type { CSSProperties } from 'react';
import { IndustryQuoteForm } from '@/components/hubspot';
import { toStorefrontMediaUrl } from '@/lib/media-url';
import { CmsImage, CtaLink, RichText, benefitIcons, resourceIcons, type IndustryBenefitsContent, type IndustryDecoderContent, type IndustryFinalQuoteContent, type IndustryIntroContent, type IndustryResourcesContent } from '../IndustryPrimitives';
import styles from './IndustryPageSections.module.css';

// Re-export shared types for callers that already import from this file.
export type { IndustryImageRef, IndustryTextSegment, IndustryLinkSegment, IndustryLineBreakSegment, IndustryRichTextSegment, IndustryIntroContent, IndustryDecoderContent, IndustryBenefitIcon, IndustryBenefitCard, IndustryBenefitsContent, IndustryTabContent, IndustryTabbedContent, IndustryGalleryCard, IndustryGalleryContent, IndustryRelatedCard, IndustryRelatedContent, IndustryResourceIcon, IndustryResourceCard, IndustryResourcesContent, IndustryFinalQuoteContent, IndustryTabbedSectionVariant } from '../IndustryPrimitives';

// Re-export interactive sections so existing imports keep working.
export { IndustryGalleryCarousel, IndustryTabbedSection } from '../IndustryInteractiveSections';
export function IndustryIntroSection(t0) {
  const $ = _c(15);
  const {
    content
  } = t0;
  let t1;
  if ($[0] !== content.eyebrow) {
    t1 = <span className={styles.eyebrow} data-cms-field="eyebrow">{content.eyebrow}</span>;
    $[0] = content.eyebrow;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== content.title) {
    t2 = <h2 className={styles.sectionTitle} data-cms-field="title">{content.title}</h2>;
    $[2] = content.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== content.body) {
    t3 = Array.isArray(content.body) ? <RichText segments={content.body} /> : content.body;
    $[4] = content.body;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <p className={styles.introBody} data-cms-field="body">{t3}</p>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t4) {
    t5 = <div className={styles.container}>{t1}{t2}{t4}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== content.blockId || $[13] !== t5) {
    t6 = <section className={styles.introSection} data-cms-block-id={content.blockId}>{t5}</section>;
    $[12] = content.blockId;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
export function IndustryDecoderSection(t0) {
  const $ = _c(18);
  const {
    content
  } = t0;
  let t1;
  if ($[0] !== content.eyebrow) {
    t1 = <span className={styles.eyebrow} data-cms-field="eyebrow">{content.eyebrow}</span>;
    $[0] = content.eyebrow;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== content.title) {
    t2 = <h2 className={styles.sectionTitle} data-cms-field="title">{content.title}</h2>;
    $[2] = content.title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== content.body) {
    t3 = <p className={styles.decoderText} data-cms-field="body">{content.body}</p>;
    $[4] = content.body;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== content.image) {
    t4 = <div className={styles.decoderImageWrap}><CmsImage image={content.image} className={styles.decoderImage} priority={true} loading="eager" fetchPriority="high" sizes="(min-width: 1024px) 64vw, 100vw" /></div>;
    $[6] = content.image;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className={styles.decoderGrid}>{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t5) {
    t6 = <div className={styles.container}>{t1}{t2}{t5}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== content.blockId || $[16] !== t6) {
    t7 = <section className={styles.decoderSection} data-cms-block-id={content.blockId}>{t6}</section>;
    $[15] = content.blockId;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
export function IndustryBenefitsSection(t0) {
  const $ = _c(10);
  const {
    content
  } = t0;
  const t1 = content.blockId;
  let t2;
  if ($[0] !== content.title) {
    t2 = <div className={styles.peachBand}><div className={styles.container}><h2 className={styles.sectionTitle} data-cms-field="title">{content.title}</h2></div></div>;
    $[0] = content.title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== content.cards) {
    t3 = content.cards.map(_temp);
    $[2] = content.cards;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <div className={styles.benefitGrid}>{t3}</div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== content.blockId || $[7] !== t2 || $[8] !== t4) {
    t5 = <section data-cms-block-id={t1}>{t2}{t4}</section>;
    $[6] = content.blockId;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function _temp(card, index) {
  const Icon = benefitIcons[card.icon];
  return <article key={card.title} className={styles.benefitCard} data-cms-field={`cards.${index}`}><div className={styles.benefitHeader}><Icon className={styles.benefitIcon} strokeWidth={1.9} /><h3 className={styles.benefitTitle} data-cms-field={`cards.${index}.title`}>{card.title}</h3></div><p className={styles.bodyText} data-cms-field={`cards.${index}.body`}><RichText segments={card.body} /></p></article>;
}
export function IndustryResourcesSection(t0) {
  const $ = _c(21);
  const {
    content
  } = t0;
  const t1 = content.blockId;
  let t2;
  if ($[0] !== content.title) {
    t2 = <h2 className={styles.sectionTitleWhite} data-cms-field="title">{content.title}</h2>;
    $[0] = content.title;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== content.paragraphs) {
    t3 = content.paragraphs.map(_temp2);
    $[2] = content.paragraphs;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== content.cta.href || $[5] !== content.cta.label) {
    t4 = <CtaLink href={content.cta.href}>{content.cta.label}</CtaLink>;
    $[4] = content.cta.href;
    $[5] = content.cta.label;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <div className={styles.resourcesCopy}>{t2}{t3}{t4}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== content.cards) {
    t6 = content.cards.map(_temp3);
    $[11] = content.cards;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <div className={styles.resourceCards}>{t6}</div>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div className={styles.resourcesGrid}>{t5}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== content.blockId || $[19] !== t8) {
    t9 = <section className={styles.resourcesSection} data-cms-block-id={t1}>{t8}</section>;
    $[18] = content.blockId;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}

// Lifted into its own component so it can be reused on CMS pages and any
// future "related cards" section. Re-exported here under the legacy name so
// existing PageContent imports continue to work unchanged.
function _temp3(card, index_0) {
  const Icon = resourceIcons[card.icon];
  return <article key={`${card.title}-${index_0}`} className={styles.resourceCard} data-cms-field={`cards.${index_0}`}><Icon className={styles.resourceIcon} strokeWidth={1.8} /><h4 className={styles.resourceTitle}>{card.title}</h4><p className={styles.resourceBody}><RichText segments={card.body} /></p></article>;
}
function _temp2(paragraph, index) {
  return <p key={paragraph} className={styles.whiteBody} data-cms-field={`paragraphs.${index}`}>{paragraph}</p>;
}
export { RelatedIndustriesSection as IndustryRelatedSection } from '../../RelatedIndustriesSection';
export function IndustryFinalQuoteSection(t0) {
  const $ = _c(33);
  const {
    content
  } = t0;
  const t1 = content.id;
  const t2 = content.blockId;
  let t3;
  if ($[0] !== content.backgroundImage) {
    t3 = toStorefrontMediaUrl(content.backgroundImage);
    $[0] = content.backgroundImage;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `url(${t3})`;
  let t5;
  if ($[2] !== t4) {
    t5 = {
      "--final-bg": t4
    };
    $[2] = t4;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const t6 = t5 as CSSProperties;
  let t7;
  if ($[4] !== content.eyebrow) {
    t7 = <span className={styles.eyebrow} data-cms-field="eyebrow">{content.eyebrow}</span>;
    $[4] = content.eyebrow;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== content.title) {
    t8 = <h2 className={styles.finalTitle} data-cms-field="title">{content.title}</h2>;
    $[6] = content.title;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== content.subheading) {
    t9 = <h4 className={styles.finalSubheading} data-cms-field="subheading">{content.subheading}</h4>;
    $[8] = content.subheading;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== content.body) {
    t10 = <p className={styles.whiteBody} data-cms-field="body">{content.body}</p>;
    $[10] = content.body;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== t10 || $[13] !== t7 || $[14] !== t8 || $[15] !== t9) {
    t11 = <div className={styles.finalCopy}>{t7}{t8}{t9}{t10}</div>;
    $[12] = t10;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== content.hubspotTitle) {
    t12 = <h2 className={styles.formTitle}>{content.hubspotTitle}</h2>;
    $[17] = content.hubspotTitle;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== content.emptyFormMethod) {
    t13 = <form method={content.emptyFormMethod} className={styles.emptySourceForm} />;
    $[19] = content.emptyFormMethod;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <IndustryQuoteForm variant="light" className={styles.finalEmbeddedForm} />;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t12 || $[23] !== t13) {
    t15 = <div className={styles.finalFormCard}>{t12}{t13}{t14}</div>;
    $[22] = t12;
    $[23] = t13;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t11 || $[26] !== t15) {
    t16 = <div className={styles.finalGrid}>{t11}{t15}</div>;
    $[25] = t11;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== content.blockId || $[29] !== content.id || $[30] !== t16 || $[31] !== t6) {
    t17 = <section id={t1} className={styles.finalQuote} data-cms-block-id={t2} style={t6}>{t16}</section>;
    $[28] = content.blockId;
    $[29] = content.id;
    $[30] = t16;
    $[31] = t6;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
}